import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Root tabs where swipe-back must NOT trigger
const ROOT_PATHS = ["/app/home", "/app/here", "/app/alerts", "/app/profile", "/app", "/"];

/**
 * Global swipe-right-to-go-back gesture (iOS style).
 * - starts only from the left 24px edge
 * - decisive horizontal swipe, ignored during vertical scroll
 * - disabled on root tabs and on gesture-heavy elements (sliders, cropper, maps, canvas)
 */
export const useSwipeBack = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const touchStartRef = useRef(null);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  useEffect(() => {
    const isBlockedTarget = (el) =>
      !!(el && el.closest && el.closest(
        'input[type="range"], canvas, video, [data-no-swipe-back], .reactEasyCrop_Container, [role="slider"], .leaflet-container, [data-radix-slider-root]'
      ));

    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      if (ROOT_PATHS.includes(pathRef.current)) {
        touchStartRef.current = null;
        return;
      }
      // Only trigger from the left 24px edge, single finger, not on gesture elements
      if (touch.clientX < 24 && e.touches.length === 1 && !isBlockedTarget(e.target)) {
        touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now(), cancelled: false };
      } else {
        touchStartRef.current = null;
      }
    };

    const handleTouchMove = (e) => {
      const s = touchStartRef.current;
      if (!s || s.cancelled) return;
      const touch = e.touches[0];
      const dx = touch.clientX - s.x;
      const dy = Math.abs(touch.clientY - s.y);
      // Cancel if the gesture is mostly vertical (user is scrolling)
      if (dy > 30 && dy > dx) s.cancelled = true;
    };

    const handleTouchEnd = (e) => {
      const s = touchStartRef.current;
      touchStartRef.current = null;
      if (!s || s.cancelled) return;
      const touch = e.changedTouches[0];
      const dx = touch.clientX - s.x;
      const dy = Math.abs(touch.clientY - s.y);
      const dt = Date.now() - s.time;
      // Decisive right swipe: ≥90px horizontal, <70px vertical, within 600ms
      if (dx > 90 && dy < 70 && dt < 600 && dx > dy * 2) {
        navigate(-1);
      }
    };

    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchmove", handleTouchMove, { passive: true });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [navigate]);
};

export default useSwipeBack;
